import React, { useEffect, useState } from 'react';
import { useService } from 'contexts/ServiceContextProvider';
import { useWorkspaces } from 'contexts/WorkspacesContextProvider';
import {
  Box,
  Paper,
  Typography,
  TextField,
  Button,
  Autocomplete,
  Tabs,
  Tab,
  ToggleButton,
  ToggleButtonGroup,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { DataGrid } from '@mui/x-data-grid';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
} from 'recharts';
import { useTranslation } from "react-i18next";
import '../../css/components/Statistics/Stats.css';
import '../../css/main.css';
import { useParams } from 'react-router-dom';
import ViewStatisticalTasks from './ViewStatisticalTasks';
import CustomBarShape from './CustomBarShape';
import TinyCustomFallback from 'components/Custom/TinyCustomFallback';

// A simple TabPanel helper component.
function TabPanel(props) {
  const { children, value, index, ...other } = props;
  return (
    <div role="tabpanel" hidden={value !== index} id={`tabpanel-${index}`} {...other}>
      {value === index && <Box sx={{ p: 2 }}>{children}</Box>}
    </div>
  );
}

// A helper function to group projects per user.
const getGroupedProjectData = (users) => {
  const projectSet = new Set();
  users.forEach((user) => {
    const tasks = user.tasks_per_project || {};
    Object.keys(tasks).forEach((projectName) => projectSet.add(projectName));
  });
  const projects = Array.from(projectSet);
  return projects.map((projectName) => {
    const entry = { project: projectName };
    users.forEach((user) => {
      entry[user.full_name] = user.tasks_per_project[projectName] || 0;
    });
    return entry;
  });
};

const StatsPage = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isBelow768 = useMediaQuery('(max-width:768px)');
  const isLargeScreen = useMediaQuery('(min-width:1300px)');


  const workspaceContext = useWorkspaces();
  const { WorkspaceStats, WorkspaceStatsTaskView } = useService();
  const myWorkspaces = workspaceContext.workspaces;
  const [myUserPerms, setMyUserPerms] = useState(null);
  const [stats, setStats] = useState(null);
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);
  const [selectedProjectId, setSelectedProjectId] = useState(null);

  const { t } = useTranslation();

  // Main tabs: admins have three, non-admins two.
  const [tabValue, setTabValue] = useState(0);
  const [dashboardView, setDashboardView] = useState('workspace');
  const [workspaceDetailView, setWorkspaceDetailView] = useState('project');
  const [selectedUserId, setSelectedUserId] = useState(null);
  const [selectedComparisonUsers, setSelectedComparisonUsers] = useState([]);
  const [projectStatusFilter, setProjectStatusFilter] = useState('ALL');

  const [openTaskModal, setOpenTaskModal] = useState(false);
  const [selectedStatType, setSelectedStatType] = useState('');
  const [myLoggedUser, setMyLoggedUser] = useState(null);

  const { workspaceId } = useParams();
  const isAdmin = myUserPerms?.is_admin || myUserPerms?.is_owner;
  
  /* eslint-disable react-hooks/exhaustive-deps */
  useEffect(() => {
    document.title = t('titles.statistics');
    if (
      !workspaceContext.loading &&
      workspaceContext.workspaceWorkers.length > 0 &&
      workspaceContext.getCurrentlyLoggedUser
    ) {
      const userPerms = workspaceContext.getLoggedUserPermissions();
      setMyLoggedUser(workspaceContext.getCurrentlyLoggedUser);
      setMyUserPerms(userPerms);
      fetchStats();
    }
  }, [
    workspaceContext.loading,
    workspaceContext.workspaceWorkers,
    workspaceContext.getCurrentlyLoggedUser,
    startDate,
    endDate,
  ]);

  const fetchStats = async () => {
    try {
      const params = {};
      if (startDate) params.start_date = startDate;
      if (endDate) params.end_date = endDate;
      const response = await WorkspaceStats(workspaceId, params);
      setStats(response);
    } catch (err) {
      console.error('Error fetching stats', err);
    }
  };

  const currentUser = workspaceContext.getCurrentlyLoggedUser() || {};

  useEffect(() => {
    if (isAdmin && stats?.user_stats?.length && !selectedUserId) {
      setSelectedUserId(myUserPerms.user_id);
    }
  }, [isAdmin, stats?.user_stats, currentUser, selectedUserId]);

  if (!stats || !stats.user_stats) {
    return (
      <Box sx={{ display: "flex", alignItems: "center", flexDirection: "column" }}>
        <Typography variant="h4" sx={{ color: '#fff' }}>
          {t("statistics.loading_stats")}
        </Typography>
        <TinyCustomFallback size_multiplier={3} />
      </Box>
    );
  }

  const effectiveUserId = isAdmin ? selectedUserId : currentUser.id;
  const selfStats =
    Array.isArray(stats.user_stats) && effectiveUserId
      ? stats.user_stats.find((u) => u.user_id === effectiveUserId)
      : stats.user_stats;

  const workspaceProjectData = stats.project_stats || [];

  // Map project data for different views.
  const mapProjectData = (dataKey) =>
    selfStats && selfStats[dataKey]
      ? Object.entries(selfStats[dataKey]).map(([projectName, count]) => {
        const project = workspaceProjectData.find((p) => p.project_name === projectName);
        return {
          name: projectName,
          value: count,
          project_id: project ? project.project_id : null,
        };
      })
      : [];

  const selfInProgressProjectData = mapProjectData('tasks_doing_per_project');
  const selfToDoProjectData = mapProjectData('tasks_to_do_per_project');
  const selfProjectData = mapProjectData('tasks_per_project');

  const filteredWorkspaceProjectData =
    projectStatusFilter === 'ALL'
      ? workspaceProjectData
      : workspaceProjectData.filter((p) => p.status === projectStatusFilter);

  const filteredSelfProjectData =
    projectStatusFilter === 'ALL'
      ? selfProjectData
      : selfProjectData.filter((item) =>
        filteredWorkspaceProjectData.some((proj) => proj.project_name === item.name)
      );

  const comparisonUsersStats =
    selectedComparisonUsers.length > 0
      ? stats.user_stats.filter((u) => selectedComparisonUsers.includes(u.user_id))
      : [];

  const groupedProjectData = getGroupedProjectData(comparisonUsersStats);
  const filteredGroupedProjectData =
    projectStatusFilter === 'ALL'
      ? groupedProjectData
      : groupedProjectData.filter((gpd) =>
        filteredWorkspaceProjectData.some((p) => p.project_name === gpd.project)
      );

  const aggregatedUserData = Array.isArray(stats.user_stats)
    ? stats.user_stats
      .filter(
        (u) => selectedComparisonUsers.length === 0 || selectedComparisonUsers.includes(u.user_id)
      )
      .map((u) => ({
        user_id: u.user_id,
        name: u.full_name,
        tasks_doing: u.tasks_doing,
        tasks_to_do: u.tasks_to_do,
      }))
    : [];

  const filteredTasksDone =
    selfStats && selfStats.tasks_per_project
      ? Object.entries(selfStats.tasks_per_project)
        .filter(([projectName]) =>
          projectStatusFilter === 'ALL' ||
          filteredWorkspaceProjectData.some((p) => p.project_name === projectName)
        )
        .reduce((acc, [, count]) => acc + count, 0)
      : 0;

  const filteredOverviewData = selfStats
    ? [
      { name: 'Tasks Completed', value: filteredTasksDone },
      { name: 'Tasks In Progress', value: selfStats.tasks_doing },
      { name: 'Tasks To Do', value: selfStats.tasks_to_do },
    ]
    : [];

  const pieColors = ['#8884d8', '#82ca9d', '#ffc658', '#ff8042', '#0088FE', '#00C49F'];

  // DataGrid columns.
  const projectColumns = [
    { field: 'project_name', headerName: 'Project Name', flex: 1 },
    { field: 'tasks_done', headerName: 'Tasks Done', flex: 1 },
    { field: 'tasks_doing', headerName: 'Tasks In Progress', flex: 1 },
    { field: 'tasks_to_do', headerName: 'Tasks To Do', flex: 1 },
  ];

  const workspaceUserColumns = [
    { field: 'full_name', headerName: 'User Name', flex: 1 },
    { field: 'tasks_done', headerName: 'Tasks Done', flex: 1 },
    { field: 'tasks_doing', headerName: 'Tasks In Progress', flex: 1 },
    { field: 'tasks_after_deadline', headerName: 'Tasks After Deadline', flex: 1 },
  ];

  const projectRows = (stats.project_stats || []).map((p) => ({
    id: p.project_id,
    ...p,
  }));
  const workspaceUserRows = Array.isArray(stats.user_stats)
    ? stats.user_stats.map((u) => ({
      id: u.user_id,
      ...u,
    }))
    : [];

  if (!selfStats || !selfStats.project_status_breakdown) {
    return (
      <Box sx={{ p: 2 }}>
        <Typography variant="h6" sx={{ color: '#fff' }}>
          Loading Detailed Self Statistics...
        </Typography>
      </Box>
    );
  }

  const handleProjectStatusFilterChange = (event) => {
    setProjectStatusFilter(event.target.value);
  };

  // Handlers for tab, dashboard view, etc.
  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
  };
  const handleDashboardViewChange = (event, newView) => {
    if (newView) setDashboardView(newView);
  };
  const handleWorkspaceDetailViewChange = (event) => {
    setWorkspaceDetailView(event.target.value);
  };

  const computeProjectCompletion = (project) => {
    const { tasks_done, tasks_doing, tasks_to_do } = project;
    const totalTasks = tasks_done + tasks_doing + tasks_to_do;
    const weightedDone = tasks_done + tasks_doing * 0.5;
    return totalTasks ? (weightedDone / totalTasks) * 100 : 0;
  };

  const projectCompletionData = filteredWorkspaceProjectData.map((project) => ({
    project_name: project.project_name,
    progress: computeProjectCompletion(project),
  }));

  const getColorForProgress = (progress) => {
    const colors = [
      { progress: 0, color: '#e74c3c' },
      { progress: 33, color: '#f39c12' },
      { progress: 66, color: '#f1c40f' },
      { progress: 100, color: '#2ecc71' },
    ];

    if (progress >= 100) return colors[colors.length - 1].color;

    let startColor, endColor;
    for (let i = 0; i < colors.length - 1; i++) {
      if (progress >= colors[i].progress && progress < colors[i + 1].progress) {
        startColor = colors[i];
        endColor = colors[i + 1];
        break;
      }
    }
    if (!startColor || !endColor) return '#000000';

    const range = endColor.progress - startColor.progress;
    const factor = (progress - startColor.progress) / range;

    const parseHex = (hex) => ({
      r: parseInt(hex.slice(1, 3), 16),
      g: parseInt(hex.slice(3, 5), 16),
      b: parseInt(hex.slice(5, 7), 16),
    });
    const startRGB = parseHex(startColor.color);
    const endRGB = parseHex(endColor.color);

    const interpolate = (start, end, factor) => Math.round(start + (end - start) * factor);
    const r = interpolate(startRGB.r, endRGB.r, factor);
    const g = interpolate(startRGB.g, endRGB.g, factor);
    const b = interpolate(startRGB.b, endRGB.b, factor);

    return `rgb(${r}, ${g}, ${b})`;
  };

  const DynamicColoredBar = (props) => {
    const { x, y, width, height, payload } = props;
    const fillColor = getColorForProgress(payload.progress);
    return <rect x={x} y={y} width={width} height={height} fill={fillColor} />;
  };

  // Define tab labels based on admin status.
  const tabs = isAdmin
    ? [
      t('statistics.dashboard'),
      t('statistics.detailed_stats'),
      t('statistics.detailed_self_stats'),
    ]
    : [t('statistics.dashboard'), t('statistics.detailed_self_stats')];

  const formatMeanTime = (meanTime, t) => {
    if (!meanTime) return 'N/A';
    const { days, hours, minutes, seconds } = meanTime;
    return `${days} ${t("statistics.days")}, ${hours} ${t("statistics.hours")}, ${minutes} ${t("statistics.minutes")}, ${seconds} ${t("statistics.seconds")}`;
  };

  
  const MAX_TICK_WIDTH = 50; 

  const renderCustomTick = (props) => {
    
    const { x, y, payload } = props;
    const fullText = payload.value;
    const canvas = document.createElement('canvas');
    const context = canvas.getContext('2d');
    context.font = '12px sans-serif';
    const fullWidth = context.measureText(fullText).width;
  
    let displayText = fullText;
    if (fullWidth > MAX_TICK_WIDTH) {
      // Remove characters until the text plus "....." fits in MAX_TICK_WIDTH
      while (
        context.measureText(displayText + '.....').width > MAX_TICK_WIDTH &&
        displayText.length > 0
      ) {
        displayText = displayText.slice(0, -1);
      }
      displayText += '...';
    }
  
    return (
      <text
        x={x}
        y={y + 10} 
        textAnchor="middle"
        fill="#fff"
        title={fullText}
      >
        {displayText}
      </text>
    );
  };
  

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns}>
      <Box sx={{ p: isMobile ? 1 : 2 }}>
        <Typography variant="h4" sx={{ color: '#fff', mb: 2, mt: isMobile ? 0 : -4 }}>
          {t("statistics.workspace_statistics")}
        </Typography>

        {/* Date Filter Controls */}
        <Box
          sx={{
            display: 'flex',
            flexDirection: isMobile ? 'column' : 'row',
            alignItems: 'center',
            mb: 2,
          }}
        >
          <TextField
            type="date"
            label={t("global.start_date_title")}
            value={startDate}
            onChange={(e) => setStartDate(e.target.value)}
            InputLabelProps={{
              shrink: true,
              className: "create-event-title",
            }}
            InputProps={{
              className: "white-txt-color",
            }}
            inputProps={{
              max: "9999-12-31",
            }}
            sx={{ mr: isMobile ? 0 : 2, mb: isMobile ? 1 : 0 }}
            fullWidth={isMobile}
          />
          <TextField
            type="date"
            label={t("global.end_date_title")}
            value={endDate}
            onChange={(e) => setEndDate(e.target.value)}
            InputLabelProps={{
              shrink: true,
              className: "create-event-title",
            }}
            InputProps={{
              className: "white-txt-color",
            }}
            inputProps={{
              max: "9999-12-31",
            }}
            sx={{ mr: isMobile ? 0 : 2, mb: isMobile ? 1 : 0 }}
            fullWidth={isMobile}
          />
          <Button
            variant="contained"
            color="secondary"
            onClick={() => {
              setStartDate('');
              setEndDate('');
            }}
            sx={{ height: 'fit-content' }}
          >
            {t("statistics.reset")}
          </Button>
        </Box>

        {/* Project Status Filter Control */}
        <FormControl sx={{ mb: 2, minWidth: isMobile ? '100%' : 200 }}>
          <InputLabel id="project-status-label">{t("statistics.project_status")}</InputLabel>
          <Select
            labelId="project-status-label"
            value={projectStatusFilter}
            label={t("statistics.project_status")}
            onChange={handleProjectStatusFilterChange}
            sx={{
              '& .MuiSelect-select': {
                color: '#fff',
              },
            }}
            MenuProps={{
              classes: {
                paper: 'dropdown-select-input-prop',
                listbox: 'dropdown-select-input-prop-listbox',
              },
            }}
          >
            <MenuItem value="ALL">ALL</MenuItem>
            <MenuItem value="ongoing">Ongoing</MenuItem>
            <MenuItem value="completed">Completed</MenuItem>
            <MenuItem value="not_started">Not Started</MenuItem>
            <MenuItem value="archived">Archived</MenuItem>
          </Select>
        </FormControl>

        {/* Main Tabs */}
        <Paper className="myTabsPaper">
          <Tabs
            value={tabValue}
            onChange={handleTabChange}
            variant={isMobile ? "scrollable" : "fullWidth"}
            scrollButtons={isMobile ? "auto" : "off"}
            className="myTabsBG"
          >
            {tabs.map((label, idx) => (
              <Tab key={idx} label={label} />
            ))}
          </Tabs>
        </Paper>

        {/* Tab Panels */}
        <TabPanel value={tabValue} index={0} className="myTabsPaper">
          {isAdmin && (
            <Box sx={{ mb: 2 }}>
              <Typography variant="h6" sx={{ color: '#fff', mb: 1 }}>
                {dashboardView === 'user' ? t("statistics.user_dashboard") : t("statistics.workspace_dashboard")}
              </Typography>
              <ToggleButtonGroup
                value={dashboardView}
                exclusive
                onChange={handleDashboardViewChange}
                sx={{ mb: 2 }}
                className="myToggleGroup"
              >
                <ToggleButton value="workspace">{t("statistics.workspace_dashboard")}</ToggleButton>
                <ToggleButton value="user">{t("statistics.user_dashboard")}</ToggleButton>
              </ToggleButtonGroup>
            </Box>
          )}

          {/* Admin User Dashboard */}
          {(!isAdmin || dashboardView === 'user') && (
            <>
              {isAdmin && (
                <Box sx={{ mb: 2, minWidth: isMobile ? '100%' : 200 }}>
                  <Autocomplete
                    options={stats.user_stats}
                    getOptionLabel={(option) => option.full_name}
                    value={stats.user_stats.find((user) => user.user_id === selectedUserId) || null}
                    onChange={(event, newValue) =>
                      setSelectedUserId(newValue ? newValue.user_id : null)
                    }
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label={t("statistics.select_user")}
                        variant="outlined"
                        InputProps={{
                          ...params.InputProps,
                          className: "dropdown-select-input-prop",
                        }}
                      />
                    )}
                    classes={{
                      paper: "dropdown-select-input-prop",
                      listbox: "dropdown-select-input-prop-listbox",
                      option: "dropdown-select-input-prop-option",
                    }}
                    fullWidth={isMobile}
                  />
                </Box>
              )}
              <Typography variant="h5" sx={{ mb: 2, color: '#fff' }}>
                {t("statistics.task_overview_for")}{" "}
                {isAdmin
                  ? stats.user_stats.find((u) => u.user_id === selectedUserId)?.full_name
                  : t("statistics.for_you")}

              </Typography>
              <ResponsiveContainer width="100%" height={isMobile ? 200 : 300}>
                <BarChart data={filteredOverviewData}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="name" stroke="#fff" />
                  <YAxis stroke="#fff" />
                  <Tooltip />
                  <Legend />
                  <Bar
                    name={t("statistics.value")}
                    dataKey="value"
                    fill="#82ca9d"
                    shape={(props) => (
                      <CustomBarShape
                        {...props}
                        chartHeight={isMobile ? 200 : 300}
                        onBarClick={(payload) => {
                          let statType = '';
                          if (payload.name === 'Tasks Completed') {
                            statType = 'completed';
                          } else if (payload.name === 'Tasks In Progress') {
                            statType = 'in_progress';
                          } else if (payload.name === 'Tasks To Do') {
                            statType = 'to_do';
                          }
                          setSelectedProjectId(null);
                          setSelectedStatType(statType);
                          setOpenTaskModal(true);
                        }}
                      />
                    )}
                  />
                </BarChart>
              </ResponsiveContainer>
              <Typography variant="h5" sx={{ mt: 4, mb: 2, color: '#fff' }}>
                {t("statistics.tasks_completed_per_proj")}
              </Typography>
              {filteredSelfProjectData.length > 0 ? (
                <ResponsiveContainer width="100%" height={isMobile ? 200 : 300} >
                  <PieChart sx={{ marginTop: "20px" }}>
                    <Pie data={filteredSelfProjectData} name={t("statistics.value")}
                      dataKey="value" nameKey="name" outerRadius={isMobile ? 50 : 80} label>
                      {filteredSelfProjectData.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={pieColors[index % pieColors.length]} />
                      ))}
                    </Pie>
                    <Tooltip />
                    <Legend />
                  </PieChart>
                </ResponsiveContainer>
              ) : (
                <Typography sx={{ color: '#fff' }}>
                  {t("statistics.no_project_data_status")}
                </Typography>
              )}

              {/* 1. Tasks Completed per Project */}
              <Typography variant="h5" sx={{ mt: 4, mb: 2, color: '#fff' }}>
                {t("statistics.tasks_completed_per_proj")}
              </Typography>
              <ResponsiveContainer width="100%" height={isMobile ? 200 : 300}>
                <BarChart data={selfProjectData}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="name" stroke="#fff" />
                  <YAxis stroke="#fff" />
                  <Tooltip />
                  <Legend />
                  <Bar
                    name={t("statistics.value")}
                    dataKey="value"
                    fill="#8884d8"
                    shape={(props) => (
                      <CustomBarShape
                        {...props}
                        chartHeight={isMobile ? 200 : 300}
                        onBarClick={(payload) => {
                          if (payload.project_id) {
                            setSelectedStatType('completed');
                            setSelectedProjectId(payload.project_id);
                            setOpenTaskModal(true);
                          } else {
                            console.error("No project_id found for payload", payload);
                          }
                        }}
                      />
                    )}
                  />
                </BarChart>
              </ResponsiveContainer>

              {/* Additional charts (tasks in progress, tasks to do) follow a similar pattern */}
              <Typography variant="h5" sx={{ mt: 4, mb: 2, color: '#fff' }}>
                {t("statistics.tasks_in_progress_per_proj")}
              </Typography>
              {selfInProgressProjectData.length > 0 ? (
                <ResponsiveContainer width="100%" height={isMobile ? 200 : 300}>
                  <BarChart data={selfInProgressProjectData}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="name" stroke="#fff" />
                    <YAxis stroke="#fff" />
                    <Tooltip />
                    <Legend />
                    <Bar
                      name={t("statistics.value")}
                      dataKey="value"
                      fill="#ff8042"
                      shape={(props) => (
                        <CustomBarShape
                          {...props}
                          chartHeight={isMobile ? 200 : 300}
                          onBarClick={(payload) => {
                            if (payload.project_id) {
                              setSelectedStatType('in_progress');
                              setSelectedProjectId(payload.project_id);
                              setOpenTaskModal(true);
                            } else {
                              console.error("No project_id found for payload", payload);
                            }
                          }}
                        />
                      )}
                    />
                  </BarChart>
                </ResponsiveContainer>
              ) : (
                <Typography sx={{ color: '#fff' }}>
                  {t("statistics.no_project_data_in_progress")}
                </Typography>
              )}

              <Typography variant="h5" sx={{ mt: 4, mb: 2, color: '#fff' }}>
                {t("statistics.tasks_to_do_per_proj")}
              </Typography>
              {selfToDoProjectData.length > 0 ? (
                <ResponsiveContainer width="100%" height={isMobile ? 200 : 300}>
                  <BarChart data={selfToDoProjectData}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="name" stroke="#fff" />
                    <YAxis stroke="#fff" />
                    <Tooltip />
                    <Legend />
                    <Bar
                      name={t("statistics.value")}
                      dataKey="value"
                      fill="#0088FE"
                      shape={(props) => (
                        <CustomBarShape
                          {...props}
                          chartHeight={isMobile ? 200 : 300}
                          onBarClick={(payload) => {
                            if (payload.project_id) {
                              setSelectedStatType('to_do');
                              setSelectedProjectId(payload.project_id);
                              setOpenTaskModal(true);
                            } else {
                              console.error("No project_id found for payload", payload);
                            }
                          }}
                        />
                      )}
                    />
                  </BarChart>
                </ResponsiveContainer>
              ) : (
                <Typography sx={{ color: '#fff' }}>
                  {t("statistics.no_project_data_to_do")}
                </Typography>
              )}
            </>
          )}

          {/* Admin Workspace Dashboard */}
          {isAdmin && dashboardView === 'workspace' && (
            <>
              <Box sx={{ mb: 2, minWidth: isMobile ? '100%' : 300 }}>
                <Autocomplete
                  multiple
                  options={stats.user_stats}
                  getOptionLabel={(option) => option.full_name}
                  value={stats.user_stats.filter((u) => selectedComparisonUsers.includes(u.user_id))}
                  onChange={(event, newValue) =>
                    setSelectedComparisonUsers(newValue.map((user) => user.user_id))
                  }
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label={t("statistics.select_users_comparison")}
                      variant="outlined"
                      InputProps={{
                        ...params.InputProps,
                        className: "dropdown-select-input-prop",
                      }}
                    />
                  )}
                  ChipProps={{
                    className: "custom-chip",
                  }}
                  classes={{
                    paper: "dropdown-select-input-prop",
                    listbox: "dropdown-select-input-prop-listbox",
                    option: "dropdown-select-input-prop-option",
                  }}
                  fullWidth={isMobile}
                />
              </Box>
              {selectedComparisonUsers.length === 0 ? (
                <>
                  <Typography variant="h5" sx={{ mt: 4, mb: 2, color: '#fff' }}>
                    {t("statistics.project_completion")}
                  </Typography>
                  {projectCompletionData.length > 0 ? (
                    <ResponsiveContainer width="100%" height={isMobile ? 200 : 300}>
                      <BarChart
                        data={projectCompletionData}
                        margin={{ top: 20, right: 30, left: 20, bottom: 5 }}
                      >
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis dataKey="project_name" stroke="#fff" tick={isLargeScreen ? renderCustomTick : undefined} interval={isLargeScreen ? 0 : undefined} />
                        <YAxis stroke="#fff" unit="%" />
                        <Tooltip formatter={(value) => `${value.toFixed(2)}%`} />
                        
                        <Bar
                          name={t("statistics.progress")}
                          dataKey="progress"
                          fill="#00C49F"
                          shape={(props) => <DynamicColoredBar {...props} />}
                          label={isBelow768 ? undefined : { position: 'top', formatter: (value) => `${value.toFixed(2)}%` }}
                        />
                      </BarChart>
                    </ResponsiveContainer>
                  ) : (
                    <Typography sx={{ color: '#fff' }}>
                      {t("statistics.no_project_completion")}
                    </Typography>
                  )}
                </>
              ) : (
                filteredGroupedProjectData.length > 0 ? (
                  <ResponsiveContainer width="100%" height={isMobile ? 200 : 300}>
                    <BarChart
                      data={filteredGroupedProjectData}
                      margin={{ top: 20, right: 30, left: 20, bottom: 5 }}
                    >
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="project" stroke="#fff" />
                      <YAxis stroke="#fff" />
                      <Tooltip />
                      <Legend />
                      {comparisonUsersStats.map((user, idx) => (
                        <Bar key={user.user_id} dataKey={user.full_name} fill={pieColors[idx % pieColors.length]} />
                      ))}
                    </BarChart>
                  </ResponsiveContainer>
                ) : (
                  <Typography sx={{ color: '#fff' }}>
                    {t("statistics.no_comparison_data")}
                  </Typography>
                )
              )}

              <Typography variant="h5" sx={{ mt: 4, mb: 2, color: '#fff' }}>
                {t("statistics.tasks_progress_overall")}
              </Typography>
              {aggregatedUserData.length > 0 ? (
                <ResponsiveContainer width="100%" height={isMobile ? 200 : 300}>
                  <BarChart
                    data={aggregatedUserData}
                    margin={{ top: 20, right: 30, left: 20, bottom: 5 }}
                  >
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="name" stroke="#fff" />
                    <YAxis stroke="#fff" />
                    <Tooltip />
                    <Legend />
                    <Bar
                      name={t("statistics.tasks_in_progress")}
                      dataKey="tasks_doing"
                      fill="#ff8042"
                      shape={(props) => (
                        <CustomBarShape
                          {...props}
                          chartHeight={isMobile ? 200 : 300}
                          onBarClick={(payload) => {
                            if (payload && payload.user_id) {
                              setSelectedUserId(payload.user_id);
                            }
                            setSelectedStatType('in_progress');
                            setOpenTaskModal(true);
                          }}
                        />
                      )}
                    />
                  </BarChart>
                </ResponsiveContainer>
              ) : (
                <Typography sx={{ color: '#fff' }}>
                  {t("statistics.no_overall_data")}
                </Typography>
              )}

              <Typography variant="h5" sx={{ mt: 4, mb: 2, color: '#fff' }}>
                {t("statistics.tasks_todo_overall")}
              </Typography>
              {aggregatedUserData.length > 0 ? (
                <ResponsiveContainer width="100%" height={isMobile ? 200 : 300}>
                  <BarChart
                    data={aggregatedUserData}
                    margin={{ top: 20, right: 30, left: 20, bottom: 5 }}
                  >
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="name" stroke="#fff" />
                    <YAxis stroke="#fff" />
                    <Tooltip />
                    <Legend />
                    <Bar
                      name={t("statistics.tasks_to_do")}
                      dataKey="tasks_to_do"
                      fill="#0088FE"
                      shape={(props) => (
                        <CustomBarShape
                          {...props}
                          chartHeight={isMobile ? 200 : 300}
                          onBarClick={(payload) => {
                            if (payload && payload.user_id) {
                              setSelectedUserId(payload.user_id);
                            }
                            setSelectedStatType('to_do');
                            setOpenTaskModal(true);
                          }}
                        />
                      )}
                    />
                  </BarChart>
                </ResponsiveContainer>
              ) : (
                <Typography sx={{ color: '#fff' }}>
                  {t("statistics.no_overall_data")}
                </Typography>
              )}
            </>
          )}
        </TabPanel>

        {/* Detailed Workspace Statistics (Admin Only) */}
        {isAdmin && (
          <TabPanel value={tabValue} index={1} className="myTabsPaper">
            <Typography variant="h5" sx={{ mb: 2, color: '#fff' }}>
              {t("statistics.workspace_statistics")}
            </Typography>
            <FormControl sx={{ mb: 2, minWidth: isMobile ? '100%' : 200 }}>
              <InputLabel id="workspace-detail-label">{t("statistics.view_by")}</InputLabel>
              <Select
                labelId="workspace-detail-label"
                value={workspaceDetailView}
                label={t("statistics.view_by")}
                onChange={handleWorkspaceDetailViewChange}
                sx={{
                  '& .MuiSelect-select': {
                    color: '#fff',
                  },
                }}
                MenuProps={{
                  classes: {
                    paper: 'dropdown-select-input-prop',
                    listbox: 'dropdown-select-input-prop-listbox',
                  },
                }}
              >
                <MenuItem value="project">{t("statistics.project")}</MenuItem>
                <MenuItem value="user">{t("statistics.user")}</MenuItem>
              </Select>
            </FormControl>
            {workspaceDetailView === 'project' ? (
              <Paper
                className="custom-data-grid"
                sx={{
                  height: isMobile ? 300 : 400,
                  backgroundColor: '#1e1e1e',
                  color: '#e0e0e0',
                  '& .MuiDataGrid-footerContainer': {
                    backgroundColor: '#1e1e1e',
                    color: '#e0e0e0',
                    justifyContent: 'center',
                  },
                  '& .MuiTablePagination-root': {
                    backgroundColor: '#1e1e1e',
                    color: '#e0e0e0',
                  },
                  '& .MuiTablePagination-selectIcon': {
                    color: '#e0e0e0',
                  },
                }}
              >
                <DataGrid
                  rows={projectRows}
                  columns={projectColumns}
                  pageSize={isMobile ? 3 : 5}
                  rowsPerPageOptions={[isMobile ? 3 : 5, 10]}
                  disableSelectionOnClick
                  sx={{
                    '& .MuiDataGrid-container--top [role="row"]': {
                      backgroundColor: '#333333',
                    },
                  }}
                />
              </Paper>
            ) : (
              <Paper
                className="custom-data-grid"
                sx={{
                  height: isMobile ? 300 : 400,
                  backgroundColor: '#1e1e1e',
                  color: '#e0e0e0',
                  '& .MuiDataGrid-footerContainer': {
                    backgroundColor: '#1e1e1e',
                    color: '#e0e0e0',
                    justifyContent: 'center',
                  },
                  '& .MuiTablePagination-root': {
                    backgroundColor: '#1e1e1e',
                    color: '#e0e0e0',
                  },
                  '& .MuiTablePagination-selectIcon': {
                    color: '#e0e0e0',
                  },
                }}
              >
                <DataGrid
                  rows={workspaceUserRows}
                  columns={workspaceUserColumns}
                  pageSize={isMobile ? 3 : 5}
                  rowsPerPageOptions={[isMobile ? 3 : 5, 10]}
                  disableSelectionOnClick
                  sx={{
                    '& .MuiDataGrid-container--top [role="row"]': {
                      backgroundColor: '#333333',
                    },
                  }}
                />
              </Paper>
            )}
          </TabPanel>
        )}

        <TabPanel value={tabValue} index={isAdmin ? 2 : 1} className="myTabsPaper">
          <Typography variant="h5" sx={{ mb: 2, color: '#fff' }}>
            {t("statistics.detailed_self_stats")}
          </Typography>
          {isAdmin && (
            <Box sx={{ mb: 2, minWidth: isMobile ? '100%' : 200 }}>
              <Autocomplete
                options={stats.user_stats}
                getOptionLabel={(option) => option.full_name}
                value={stats.user_stats.find((user) => user.user_id === selectedUserId) || null}
                onChange={(event, newValue) =>
                  setSelectedUserId(newValue ? newValue.user_id : null)
                }
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label={t("statistics.select_user")}
                    variant="outlined"
                    InputProps={{
                      ...params.InputProps,
                      className: "dropdown-select-input-prop",
                    }}
                  />
                )}
                classes={{
                  paper: "dropdown-select-input-prop",
                  listbox: "dropdown-select-input-prop-listbox",
                  option: "dropdown-select-input-prop-option",
                }}
                fullWidth={isMobile}
              />
            </Box>
          )}

          {/* Projects Overview */}
          <Typography variant="h5" sx={{ mb: 2, color: '#fff' }}>
            {t("statistics.projects_overview")}
          </Typography>
          <ResponsiveContainer width="100%" height={isMobile ? 200 : 300}>
            <BarChart
              data={[
                { status: 'Ongoing', count: selfStats.project_status_breakdown.ongoing },
                { status: 'Completed', count: selfStats.project_status_breakdown.completed },
                { status: 'Not Started', count: selfStats.project_status_breakdown.not_started },
                { status: 'Archived', count: selfStats.project_status_breakdown.archived },
              ]}
              margin={{ top: 20, right: 30, left: 20, bottom: 5 }}
            >
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="status" stroke="#fff" />
              <YAxis stroke="#fff" />
              <Tooltip />
              <Legend />
              <Bar name={t("statistics.count")} dataKey="count" fill="#8884d8" />
            </BarChart>
          </ResponsiveContainer>

          {/* Tasks by Label */}
          <Typography variant="h5" sx={{ mt: 4, mb: 2, color: '#fff' }}>
            {t("statistics.tasks_by_label")}
          </Typography>
          {Object.keys(selfStats.label_task_counts).length > 0 ? (
            <ResponsiveContainer width="100%" height={isMobile ? 200 : 300}>
              <BarChart
                data={Object.entries(selfStats.label_task_counts).map(([label, count]) => ({ label, count }))}
                margin={{ top: 20, right: 30, left: 20, bottom: 5 }}
              >
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="label" stroke="#fff" />
                <YAxis stroke="#fff" />
                <Tooltip />
                <Legend />
                <Bar name={t("statistics.count")} dataKey="count" fill="#82ca9d" />
              </BarChart>
            </ResponsiveContainer>
          ) : (
            <Typography sx={{ color: '#fff' }}>{t("statistics.no_label_data")}</Typography>
          )}

          {/* Other Metrics */}
          <Box sx={{ display: 'flex', flexDirection: isMobile ? 'column' : 'row', justifyContent: 'space-around', mt: 4 }}>
            <Box sx={{ mb: isMobile ? 2 : 0 }}>
              <Typography variant="h6" sx={{ color: '#fff' }}>
                {t("statistics.tasks_finished_after_deadline")}
              </Typography>
              <Typography variant="h4" sx={{ color: '#fff' }}>
                {selfStats.tasks_after_deadline}
              </Typography>
            </Box>
            <Box>
              <Typography variant="h6" sx={{ color: '#fff' }}>
                {t("statistics.avg_task_dur")}
              </Typography>
              <Typography variant="h4" sx={{ color: '#fff' }}>
                {formatMeanTime(selfStats.mean_time_per_task, t)}
              </Typography>
            </Box>
          </Box>
        </TabPanel>
      </Box>
      {openTaskModal && (
        <ViewStatisticalTasks
          open={openTaskModal}
          handleClose={() => setOpenTaskModal(false)}
          t={t}
          statType={selectedStatType}
          workspaceId={myWorkspaces[0].id}
          userId={effectiveUserId}
          selectedProjectId={selectedProjectId}
          WorkspaceStatsTaskView={WorkspaceStatsTaskView}
          workspaceWorkers={workspaceContext.workspaceWorkers}
          myUserTimezone={myLoggedUser.timezone}
          startDate={startDate}
          endDate={endDate}
        />
      )}
    </LocalizationProvider>
  );
};

export default StatsPage;
